import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../useTranslation';
import LoginModal from './LoginModal';
import RegisterModal from './RegisterModal';
import ForgotPasswordModal from './ForgotPasswordModal';
import { useAppLanguage } from '../../hooks/useAppLanguage';
import ThemeToggle from '../ThemeToggle';
import {
    readStoredTheme,
    applyThemeToDocument,
    persistTheme,
    type ThemeMode,
} from '../../utils/themeStorage';

/** Экран входа: форма по центру без серой «полосы» снизу на мобилке. */
const AuthGate: React.FC<{ loading?: boolean }> = ({ loading: authLoading }) => {
    const [showRegister, setShowRegister] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const { language, setLanguage } = useAppLanguage();
    const [theme, setThemeState] = useState<ThemeMode>(readStoredTheme);
    const { t } = useTranslation(language);

    const setTheme = (next: ThemeMode) => {
        setThemeState(next);
        applyThemeToDocument(next);
        persistTheme(next);
    };

    useEffect(() => {
        applyThemeToDocument(theme);
    }, [theme]);

    return (
        <div className="min-h-dvh min-h-screen relative flex flex-col items-center justify-center px-4 py-10 sm:p-6 safe-area-padding bg-gradient-to-b from-white via-sky-50/80 to-stone-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(56,189,248,0.12),_transparent_55%)] dark:bg-[radial-gradient(ellipse_at_top,_rgba(56,189,248,0.08),_transparent_50%)]" />
            <div className="absolute top-4 right-4 z-20 sm:top-6 sm:right-6">
                <ThemeToggle theme={theme} setTheme={setTheme} language={language} compact />
            </div>
            <div className="relative z-10 w-full max-w-md flex justify-center my-auto">
                {authLoading ? (
                    <div className="rounded-2xl border border-stone-200/80 dark:border-slate-600/80 bg-white/95 dark:bg-slate-800/95 p-8 flex flex-col items-center gap-4 min-w-[280px] sm:min-w-[320px] w-full max-w-md shadow-xl backdrop-blur-xl">
                        <img src="/logo-email.webp" alt="MyTravelDiary" className="h-14 w-auto object-contain" />
                        <div className="animate-spin rounded-full h-10 w-10 border-2 border-stone-300 border-t-blue-600" />
                        <p className="text-stone-600 dark:text-slate-400 text-sm">{t.loading}</p>
                    </div>
                ) : showForgotPassword ? (
                    <ForgotPasswordModal
                        isOpen
                        onClose={() => setShowForgotPassword(false)}
                        inline
                        language={language}
                        onLanguageChange={setLanguage}
                        theme={theme}
                        onThemeChange={setTheme}
                    />
                ) : showRegister ? (
                    <RegisterModal
                        isOpen
                        onClose={() => setShowRegister(false)}
                        onSwitchToLogin={() => setShowRegister(false)}
                        hideCancel
                        inline
                        language={language}
                        onLanguageChange={setLanguage}
                        theme={theme}
                        onThemeChange={setTheme}
                    />
                ) : (
                    <LoginModal
                        isOpen
                        onClose={() => {}}
                        onSwitchToRegister={() => setShowRegister(true)}
                        onForgotPassword={() => setShowForgotPassword(true)}
                        hideCancel
                        inline
                        language={language}
                        onLanguageChange={setLanguage}
                        theme={theme}
                        onThemeChange={setTheme}
                    />
                )}
            </div>
        </div>
    );
};

export default AuthGate;
